import { useState } from "react";
import axios from "axios";
import { ChefHat, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KITCHEN_TYPES = ["Straight", "L-Shaped", "Parallel"];
const FINISHES = { Laminate: 1400, Acrylic: 2000 };
const DRAWERS = { Tandem: 25000, "SS Wire Basket (304 Grade)": 15000 };

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const Chip = ({ active, onClick, testId, children }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`px-4 py-2.5 text-sm font-medium border transition-colors ${
      active
        ? "bg-[#0D2121] text-[#FAF6F0] border-[#0D2121]"
        : "bg-[#FAF6F0] text-[#0D2121] border-[#0D2121]/15 hover:border-[#E2A435]"
    }`}
  >
    {children}
  </button>
);

export const KitchenCalculator = () => {
  const [ktype, setKtype] = useState("Straight");
  const [x, setX] = useState("");
  const [y, setY] = useState("");
  const [finish, setFinish] = useState("Laminate");
  const [drawers, setDrawers] = useState("Tandem");
  const [step, setStep] = useState("select"); // select -> lead -> result
  const [lead, setLead] = useState({ name: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [cost, setCost] = useState(null);

  const needsY = ktype !== "Straight";
  const xVal = parseFloat(x);
  const yVal = parseFloat(y);
  const inputsValid = xVal > 0 && (!needsY || yVal > 0);

  const computeCost = () =>
    Math.round(
      (needsY ? xVal + yVal : xVal) * 7.5 * FINISHES[finish] + DRAWERS[drawers]
    );

  const startLead = () => {
    if (!inputsValid) return;
    setCost(computeCost());
    setStep("lead");
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/kitchen-leads`, {
        name: lead.name,
        phone: lead.phone,
        kitchen_type: ktype,
        x_side: xVal,
        y_side: needsY ? yVal : null,
        finish,
        drawers,
        cost,
      });
    } catch (err) {
      console.error("Kitchen lead capture failed", err);
    } finally {
      setSubmitting(false);
      setStep("result");
    }
  };

  const getQuote = () => {
    const dims = needsY ? `X=${xVal}ft, Y=${yVal}ft` : `X=${xVal}ft`;
    const message = `Kitchen calculator estimate: ${ktype} kitchen (${dims}), ${finish} finish, ${drawers} drawers -> approx Rs. ${cost?.toLocaleString("en-IN")}. I'd like an exact quote.`;
    window.dispatchEvent(new CustomEvent("griham:prefill", { detail: message }));
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  const labelClass = "block text-xs uppercase tracking-wider text-[#7A6F66] mb-2 font-semibold";
  const inputClass =
    "w-full px-4 py-3 bg-[#FAF6F0] border border-[#0D2121]/20 text-sm text-[#0D2121] focus:outline-none focus:border-[#E2A435]";

  return (
    <section id="kitchen-calculator" className="py-24 px-6 md:px-10 bg-[#EADBC8] border-y-4 border-[#E2A435]" data-testid="kitchen-calculator-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <Reveal className="lg:col-span-5 space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0D2121] text-[#E2A435] text-xs uppercase tracking-widest font-semibold">
            <ChefHat size={14} />
            Most Requested
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#0D2121] leading-tight">
            Modular Kitchen Cost Calculator
          </h2>
          <p className="text-[#7A6F66] text-base leading-relaxed">
            Kitchens are the heart of an Indian home — and the room everyone asks us to price first. Pick your layout, slab length, finish, and drawer hardware to see an honest working estimate.
          </p>
          <ul className="space-y-3 text-sm text-[#0D2121]">
            <li className="flex gap-3"><span className="w-2 h-2 rounded-full bg-[#E2A435] mt-1.5 shrink-0"></span>Covers cabinets, shutters, countertop hardware, and fittings</li>
            <li className="flex gap-3"><span className="w-2 h-2 rounded-full bg-[#E2A435] mt-1.5 shrink-0"></span>Finish and drawer hardware priced per real site rates</li>
            <li className="flex gap-3"><span className="w-2 h-2 rounded-full bg-[#E2A435] mt-1.5 shrink-0"></span>Final quote confirmed after a free kitchen measurement visit</li>
          </ul>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={120}>
          <div className="bg-[#FAF6F0] border border-[#0D2121]/10 shadow-sm p-8 sm:p-10">
            {step === "select" && (
              <div className="space-y-6">
                <div>
                  <span className={labelClass}>Type of Kitchen</span>
                  <div className="flex flex-wrap gap-2">
                    {KITCHEN_TYPES.map((k) => (
                      <Chip key={k} active={ktype === k} onClick={() => setKtype(k)} testId={`kitchen-type-${slug(k)}`}>
                        {k}
                      </Chip>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass} htmlFor="kitchen-x">X Side (in feet)</label>
                    <input
                      id="kitchen-x"
                      type="number"
                      min="1"
                      step="0.5"
                      value={x}
                      onChange={(e) => setX(e.target.value)}
                      placeholder="e.g. 8"
                      data-testid="kitchen-x-input"
                      className={inputClass}
                    />
                  </div>
                  {needsY && (
                    <div>
                      <label className={labelClass} htmlFor="kitchen-y">Y Side (in feet)</label>
                      <input
                        id="kitchen-y"
                        type="number"
                        min="1"
                        step="0.5"
                        value={y}
                        onChange={(e) => setY(e.target.value)}
                        placeholder="e.g. 6"
                        data-testid="kitchen-y-input"
                        className={inputClass}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <span className={labelClass}>Preferred Finish</span>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(FINISHES).map((f) => (
                      <Chip key={f} active={finish === f} onClick={() => setFinish(f)} testId={`kitchen-finish-${slug(f)}`}>
                        {f}
                      </Chip>
                    ))}
                  </div>
                </div>

                <div>
                  <span className={labelClass}>Preferred Drawers</span>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(DRAWERS).map((d) => (
                      <Chip key={d} active={drawers === d} onClick={() => setDrawers(d)} testId={`kitchen-drawers-${slug(d)}`}>
                        {d}
                      </Chip>
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  disabled={!inputsValid}
                  data-testid="kitchen-calculate-btn"
                  onClick={startLead}
                  className="w-full py-4 bg-[#E2A435] hover:bg-[#c88c2a] disabled:opacity-50 text-[#0D2121] font-semibold text-sm uppercase tracking-wider transition-colors"
                >
                  Calculate My Kitchen Cost
                </button>
              </div>
            )}

            {step === "lead" && (
              <form className="space-y-4" onSubmit={submitLead} data-testid="kitchen-lead-form">
                <p className="text-sm text-[#7A6F66] leading-relaxed">
                  Almost there — share your details to view your kitchen estimate. We will only call you about your project.
                </p>
                <div>
                  <label className={labelClass} htmlFor="kitchen-lead-name">Your Name</label>
                  <input
                    id="kitchen-lead-name"
                    type="text"
                    required
                    value={lead.name}
                    onChange={(e) => setLead({ ...lead, name: e.target.value })}
                    placeholder="e.g. Aditi Sharma"
                    data-testid="kitchen-lead-name-input"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="kitchen-lead-phone">Phone Number</label>
                  <input
                    id="kitchen-lead-phone"
                    type="tel"
                    required
                    minLength={10}
                    value={lead.phone}
                    onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                    placeholder="e.g. +91 98765 43210"
                    data-testid="kitchen-lead-phone-input"
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="kitchen-lead-submit-btn"
                    className="flex-1 py-4 bg-[#E2A435] hover:bg-[#c88c2a] disabled:opacity-60 text-[#0D2121] font-semibold text-sm uppercase tracking-wider transition-colors"
                  >
                    {submitting ? "Please wait..." : "Show My Estimate"}
                  </button>
                  <button
                    type="button"
                    data-testid="kitchen-lead-back-btn"
                    onClick={() => setStep("select")}
                    className="px-6 py-4 border border-[#0D2121]/25 text-[#0D2121] text-sm font-semibold uppercase tracking-wider hover:bg-[#EADBC8] transition-colors"
                  >
                    Back
                  </button>
                </div>
              </form>
            )}

            {step === "result" && (
              <div className="space-y-6">
                <div className="bg-[#0D2121] text-[#FAF6F0] p-6 text-center space-y-3" data-testid="kitchen-result-panel">
                  <p className="text-xs uppercase tracking-widest text-[#EADBC8]/70">
                    {ktype} &middot; X {xVal} ft{needsY ? ` + Y ${yVal} ft` : ""} &middot; {finish} &middot; {drawers}
                  </p>
                  <div className="font-display text-4xl sm:text-5xl text-[#E2A435]" data-testid="kitchen-result-text">
                    ₹{cost?.toLocaleString("en-IN")}
                  </div>
                  <p className="text-[11px] text-[#EADBC8]/60 leading-relaxed">
                    Working estimate for your modular kitchen. Final quote depends on site measurements, appliance choices, and accessories.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    data-testid="kitchen-get-quote-btn"
                    onClick={getQuote}
                    className="flex-1 inline-flex items-center justify-center gap-2 py-4 bg-[#E2A435] hover:bg-[#c88c2a] text-[#0D2121] font-semibold text-sm uppercase tracking-wider transition-colors"
                  >
                    Get Exact Quote
                    <ArrowRight size={16} />
                  </button>
                  <button
                    type="button"
                    data-testid="kitchen-recalculate-btn"
                    onClick={() => setStep("select")}
                    className="px-6 py-4 border border-[#0D2121]/25 text-[#0D2121] text-sm font-semibold uppercase tracking-wider hover:bg-[#EADBC8] transition-colors"
                  >
                    Recalculate
                  </button>
                </div>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
